/**
 * Textarea Component
 *
 * Multi-line text input with consistent styling.
 */

import { forwardRef, type TextareaHTMLAttributes } from 'react';

import { cn } from '../../utils/cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Whether the textarea has an error state */
  error?: boolean;
  /** Label text displayed above textarea */
  label?: string;
  /** Helper/hint text below textarea */
  helpText?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, label, helpText, id, ...props }, ref) => {
    const textareaId = id || (label ? `textarea-${Math.random().toString(36).substr(2, 9)}` : undefined);
    return (
      <div className="w-full">
        {label && (
          <label htmlFor={textareaId} className="mb-1 block text-sm font-medium text-mv-gray-700">
            {label}
          </label>
        )}
        <textarea
          id={textareaId}
          ref={ref}
          className={cn(
            'flex min-h-[80px] w-full rounded border bg-white px-3 py-2 text-sm',
            'placeholder:text-mv-gray-400',
            'focus:border-mv-blue-500 focus:outline-none focus:ring-2 focus:ring-mv-blue-500/20',
            'disabled:cursor-not-allowed disabled:bg-mv-gray-50 disabled:opacity-50',
            'resize-y',
            error
              ? 'border-mv-red-600 focus:border-mv-red-600 focus:ring-mv-red-600/20'
              : 'border-mv-gray-300',
            className
          )}
          {...props}
        />
        {helpText && <p className="mt-1 text-xs text-mv-gray-500">{helpText}</p>}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
